import { Folder } from "lucide-react";
import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { BucketContext, ParsedFileInfo } from "../../../../types/blob";
import FileActionsMenu from "./FileActionsMenu";
import FileThumbnail from "./FileThumbnail";

interface FileRowProps {
  file: ParsedFileInfo;
  context: BucketContext;
  isFolder?: boolean;
  onNavigate?: () => void;
  onPreview: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onCopyUrl: () => void;
}

const FileRow: React.FC<FileRowProps> = ({
  file,
  context,
  isFolder = false,
  onNavigate,
  onPreview,
  onDownload,
  onDelete,
  onCopyUrl,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const handleRowClick = () => {
    if (isFolder && onNavigate) {
      onNavigate();
    } else if (file.isPreviewable) {
      onPreview();
    }
  };

  return (
    <tr
      onClick={handleRowClick}
      className={`${
        isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"
      } ${isFolder || file.isPreviewable ? "cursor-pointer" : ""}`}
    >
      {/* Name */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          {isFolder ? (
            <div
              className={`w-10 h-10 rounded flex items-center justify-center ${
                isDarkMode ? "bg-amber-900/30" : "bg-amber-100"
              }`}
            >
              <Folder
                className={`w-5 h-5 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}
              />
            </div>
          ) : (
            <FileThumbnail
              fileName={file.displayName}
              contentType={file.contentType}
              context={context}
              fileSubfolder={file.fileSubfolder}
            />
          )}
          <div>
            <p
              className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}
            >
              {file.displayName}
            </p>
            {file.metadata?.uploadedBy && (
              <p
                className={`text-xs ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
              >
                by {file.metadata.uploadedBy}
              </p>
            )}
          </div>
        </div>
      </td>

      {/* Size */}
      <td
        className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
      >
        {isFolder ? "-" : file.formattedSize}
      </td>

      {/* Type */}
      <td
        className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
      >
        {isFolder ? "folder" : file.contentType.split("/")[1] || file.contentType}
      </td>

      {/* Modified */}
      <td
        className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
      >
        {file.formattedDate}
      </td>

      {/* Actions */}
      <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
        {!isFolder && (
          <FileActionsMenu
            onPreview={onPreview}
            onDownload={onDownload}
            onDelete={onDelete}
            onCopyUrl={onCopyUrl}
            isPreviewable={file.isPreviewable}
          />
        )}
      </td>
    </tr>
  );
};

export default FileRow;

